import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  FileText, MapPin, Star, TrendingUp, AlertTriangle,
  CheckCircle2, ArrowRight, ShieldCheck, Zap, DollarSign
} from 'lucide-react';
import { useNavigate } from 'react-router';

gsap.registerPlugin(ScrollTrigger);

const reportData = {
  parcelId: 'YOR-73509-001',
  county: 'York County, SC',
  acreage: 8.7,
  marketRange: '$285K – $340K',
  wholesaleRange: '$195K – $245K',
  score: 87,
  grade: 'Strong Buy',
  growthSignals: [
    'Sewer expansion planned within 0.8 miles',
    'Subdivision pressure up +22% YoY',
    'New elementary school approved 1.2 mi away',
    'Commercial development permit filed adjacent tract',
  ],
  riskFlags: [
    { label: 'Flood zone', value: 'Zone X (minimal risk)', safe: true },
    { label: 'Wetlands', value: 'None detected', safe: true },
    { label: 'Road access', value: 'Paved frontage on 2 sides', safe: true },
    { label: 'Zoning', value: 'A-1 Agricultural / Residential', safe: true },
  ],
  whyHigh: 'This parcel scores high due to dual road frontage, low flood risk, proximity to planned sewer expansion, and strong subdivision pressure in a corridor where comparable lots have appreciated 18% in 12 months.',
  nextStep: 'Contact the owner (held 14 years, no liens) with a wholesale offer in the $210K range. Expect 30-45 day close.',
};

export default function SampleParcelReportV2() {
  const sectionRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const ctx = gsap.context(() => {
      gsap.fromTo('.report-card', { y: 30, opacity: 0 }, {
        y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'power3.out',
        scrollTrigger: { trigger: section, start: 'top 75%', once: true },
      });
    }, section);
    return () => ctx.revert();
  }, []);

  return (
    <section id="sample-report" ref={sectionRef} className="relative py-20" style={{ background: '#07110D' }}>
      <div className="mx-auto max-w-[900px] px-6">
        <div className="mb-10 text-center">
          <span className="label-accent mb-4 block" style={{ color: '#F7F8F4' }}>SAMPLE REPORT</span>
          <h2 className="font-display text-[clamp(28px,4vw,48px)] font-semibold leading-[1.1] text-[#F7F8F4]">
            See What You Get Per Parcel
          </h2>
        </div>

        {/* Report Card */}
        <div className="report-card rounded-[2rem] border border-white/10 p-6 md:p-8" style={{ background: '#101D29' }}>
          {/* Header */}
          <div className="flex flex-col gap-4 border-b border-white/10 pb-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F7F8F4] text-[#07110D]">
                <FileText size={22} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-[#8A9BAE]" />
                  <span className="text-sm text-[#a9b4c0]">{reportData.county}</span>
                </div>
                <p className="font-mono text-xs text-[#8A9BAE]">ID: {reportData.parcelId}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-[#8A9BAE]">Acreage</p>
                <p className="text-lg font-bold text-[#F7F8F4]">{reportData.acreage} ac</p>
              </div>
              <div className="h-8 w-px bg-white/10" />
              <div className="text-right">
                <p className="text-xs text-[#8A9BAE]">Score</p>
                <p className="font-display text-2xl font-black text-[#F7F8F4]">{reportData.score}</p>
              </div>
            </div>
          </div>

          {/* Price Ranges */}
          <div className="grid gap-3 py-6 grid-cols-1 sm:gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-white/10 bg-[#07110D] p-4">
              <p className="text-xs text-[#8A9BAE]">Estimated Market Range</p>
              <p className="mt-1 text-xl font-bold text-[#F7F8F4]">{reportData.marketRange}</p>
            </div>
            <div className="rounded-xl border border-[#F7F8F4]/20 bg-[#F7F8F4]/[0.04] p-4">
              <p className="text-xs text-[#F7F8F4]">Suggested Wholesale Offer</p>
              <p className="mt-1 text-xl font-black text-[#F7F8F4]">{reportData.wholesaleRange}</p>
            </div>
          </div>

          {/* Score Badge */}
          <div className="mb-6 flex items-center gap-3 rounded-xl border border-[#F7F8F4]/20 bg-[#F7F8F4]/[0.06] px-4 py-3">
            <Star size={18} className="text-[#F7F8F4]" />
            <span className="text-sm font-black text-[#F7F8F4]">{reportData.grade}</span>
            <span className="text-xs text-[#8A9BAE]">— PLP Opportunity Score {reportData.score}/100</span>
          </div>

          {/* Two Column: Growth + Risk */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Growth Signals */}
            <div className="rounded-xl bg-[#07110D] p-4">
              <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-[#F7F8F4]">
                <TrendingUp size={14} />
                Growth Signals
              </div>
              <div className="space-y-2">
                {reportData.growthSignals.map((signal) => (
                  <div key={signal} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-[#22C55E]" />
                    <span className="text-xs text-[#a9b4c0]">{signal}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Flags */}
            <div className="rounded-xl bg-[#07110D] p-4">
              <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-[#22C55E]">
                <ShieldCheck size={14} />
                Risk Assessment
              </div>
              <div className="space-y-2">
                {reportData.riskFlags.map((flag) => (
                  <div key={flag.label} className="flex items-center justify-between">
                    <span className="text-xs text-[#8A9BAE]">{flag.label}</span>
                    <span className="text-xs font-bold text-[#22C55E]">{flag.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Why This Scored High */}
          <div className="mt-4 rounded-xl border border-[#F7F8F4]/10 bg-[#F7F8F4]/[0.03] p-4">
            <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-[#FFD54A]">
              <Zap size={14} />
              Why This Parcel Scored High
            </div>
            <p className="text-xs leading-relaxed text-[#a9b4c0]">{reportData.whyHigh}</p>
          </div>

          {/* Recommended Next Step */}
          <div className="mt-4 rounded-xl border border-[#22C55E]/10 bg-[#22C55E]/[0.03] p-4">
            <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-[#22C55E]">
              <ArrowRight size={14} />
              Recommended Next Step
            </div>
            <p className="text-xs leading-relaxed text-[#a9b4c0]">{reportData.nextStep}</p>
          </div>
        </div>

        {/* CTA */}
        <div className="report-card mt-6 text-center">
          <button onClick={() => navigate('/dashboard?demo=true')} className="inline-flex items-center gap-2 rounded-2xl bg-[#F7F8F4] px-8 py-4 text-sm font-black text-[#07110D] shadow-[0_18px_50px_rgba(255,255,255,0.22)] transition hover:-translate-y-0.5 hover:brightness-105">
            <DollarSign size={16} />
            Run This Search On Your Target County
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}
